import React, { useState, useEffect } from 'react';
import { Plus, Save, Trash2, X, AlertCircle, Globe, Clock, Edit3 } from 'lucide-react';
import { Button } from "../../ui/button";
import { getThemeClass } from '../../../utils/theme';
import useBlogPosts from './hooks/useBlogPosts';
import PostList from './components/PostList';
import PostForm from './components/PostForm';
import MarkdownEditor from './components/MarkdownEditor';
import PostPreview from './components/PostPreview';

/**
 * BlogEditorContent - Content-only version for AdminPanel
 */
export const BlogEditorContent = ({ theme = 'dark', initialPostId = null }) => {
  const {
    posts,
    selectedPostId,
    selectedPost,
    loading,
    saving,
    error,
    hasChanges,
    setSelectedPostId,
    updatePostField,
    createPost,
    savePost,
    deletePost,
    togglePublish,
    revertChanges
  } = useBlogPosts();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Select initial post if provided
  useEffect(() => {
    if (initialPostId && posts.length > 0) {
      setSelectedPostId(initialPostId);
    }
  }, [initialPostId, posts, setSelectedPostId]);

  const handleCreate = async () => {
    try {
      await createPost();
    } catch (err) {
      // Error already set in hook
    }
  };

  const handleSave = async () => {
    try {
      await savePost();
    } catch (err) {
      // Error already set in hook
    }
  };

  const handleDelete = async () => {
    try {
      await deletePost();
      setShowDeleteConfirm(false);
    } catch (err) {
      // Error already set in hook
    }
  };

  return (
    <div className="h-full flex flex-col relative">
      {/* Header */}
      <div className={`flex-none flex items-center justify-between p-4 border-b ${getThemeClass(theme, 'border')}`}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Edit3 size={20} className="text-blue-400" />
            <h2 className={`text-lg font-semibold ${getThemeClass(theme, 'text.primary')}`}>
              Blog Editor
            </h2>
          </div>
          <Button
            onClick={handleCreate}
            disabled={saving}
            className={`${getThemeClass(theme, 'button.primary')} inline-flex items-center`}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {/* Preview Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className={getThemeClass(theme, 'button.outline')}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>

          {/* Change indicator */}
          {hasChanges && (
            <span className="text-sm text-amber-400">
              Unsaved changes
            </span>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex-none mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 min-h-0 flex">
        {/* Left Sidebar - Post List */}
        <div className={`w-64 flex-shrink-0 border-r ${getThemeClass(theme, 'border')}`}>
          {loading ? (
            <div className={`p-4 text-center ${getThemeClass(theme, 'text.secondary')}`}>
              Loading posts...
            </div>
          ) : (
            <PostList
              posts={posts}
              selectedPostId={selectedPostId}
              onSelect={setSelectedPostId}
              theme={theme}
            />
          )}
        </div>

        {/* Right Content Area */}
        <div className="flex-1 min-w-0 flex flex-col">
          {selectedPost ? (
            <>
              {/* Post Form */}
              <div className={`flex-none border-b ${getThemeClass(theme, 'border')}`}>
                <PostForm
                  post={selectedPost}
                  onChange={updatePostField}
                  theme={theme}
                />
              </div>

              {/* Editor and Preview */}
              <div className="flex-1 min-h-0 flex">
                {/* Markdown Editor */}
                <div className={`${showPreview ? 'w-1/2' : 'flex-1'} min-w-0 ${showPreview ? `border-r ${getThemeClass(theme, 'border')}` : ''}`}>
                  <MarkdownEditor
                    content={selectedPost.content}
                    onChange={(value) => updatePostField('content', value)}
                    theme={theme}
                  />
                </div>

                {/* Preview */}
                {showPreview && (
                  <div className="w-1/2 flex-shrink-0">
                    <PostPreview
                      post={selectedPost}
                      theme={theme}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={`flex-1 flex items-center justify-center ${getThemeClass(theme, 'text.secondary')}`}>
              <div className="text-center">
                <p className="mb-2">Select a post from the list</p>
                <p className="text-sm opacity-75">or create a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={`flex-none border-t p-4 flex justify-between ${getThemeClass(theme, 'border')}`}>
        <div className="flex space-x-2">
          {selectedPost && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving}
                className="text-red-400 border-red-400/30 hover:bg-red-400/10 inline-flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
              <Button
                variant="outline"
                onClick={togglePublish}
                disabled={saving}
                className={`inline-flex items-center ${
                  selectedPost.status === 'PUBLISHED'
                    ? 'text-amber-400 border-amber-400/30 hover:bg-amber-400/10'
                    : 'text-green-400 border-green-400/30 hover:bg-green-400/10'
                }`}
              >
                {selectedPost.status === 'PUBLISHED' ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    Unpublish
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 mr-2" />
                    Publish
                  </>
                )}
              </Button>
            </>
          )}
        </div>
        <div className="flex space-x-4">
          {hasChanges && (
            <Button
              variant="outline"
              onClick={revertChanges}
              disabled={saving}
              className={`${getThemeClass(theme, 'button.outline')} inline-flex items-center`}
            >
              Discard Changes
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`${getThemeClass(theme, 'button.primary')} inline-flex items-center`}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 z-60 flex items-center justify-center bg-black/50 rounded-lg">
          <div className={`p-6 rounded-lg shadow-xl max-w-md ${getThemeClass(theme, 'panel')} ${getThemeClass(theme, 'border')}`}>
            <h3 className={`text-lg font-semibold mb-2 ${getThemeClass(theme, 'text.primary')}`}>
              Delete Post?
            </h3>
            <p className={`mb-4 ${getThemeClass(theme, 'text.secondary')}`}>
              Are you sure you want to delete "{selectedPost?.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className={getThemeClass(theme, 'button.outline')}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * BlogEditor - Full modal version (backward compatible)
 */
const BlogEditor = ({ isOpen, onClose, initialPostId = null, theme = 'dark' }) => {
  const {
    hasChanges
  } = useBlogPosts();

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Discard them?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className={`relative rounded-lg shadow-xl w-[95vw] h-[90vh] max-w-7xl flex flex-col
                      ${getThemeClass(theme, 'panel')} ${getThemeClass(theme, 'border')}`}>
        <BlogEditorContent theme={theme} initialPostId={initialPostId} />
        
        {/* Close button overlay */}
        <button
          onClick={handleClose}
          className={`absolute top-4 right-4 p-1 rounded-full transition-colors z-10 ${getThemeClass(theme, 'hover.background')}`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default BlogEditor;
